/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { formatDate } from 'modules/dashboard/functions';
import { TIME_PATTERN, YEAR_MONTH_DAY_PATTERN } from 'modules/common/constants/date-range';
import { isEmpty } from 'modules/common/helpers/object';
import { models } from 'powerbi-client';
import { format } from 'date-fns';
import {
  createCeruleanBreakdownSchemaFilter,
  createCeruleanDateFilter,
  createCeruleanFilter,
} from '../trend-tabular-tabs/function';
import {
  BREAKDOWN_LIST_FILTER,
  CURRENCY_FILTER,
  PMS_FILTER,
} from '../trend-tabular-tabs/constants';
import {
  BOOKING_DATE_LOWER,
  BOOKING_DATE_UPPER,
  COMPARE_STAY_DATE_LOWER,
  COMPARE_STAY_DATE_UPPER,
  HOTEL_COMP_FILTER,
  HOTEL_FILTER,
  STAY_DATE_LOWER,
  STAY_DATE_UPPER,
} from '../trend-tabs/constants';
import {
  BOOKING_DATE_RANGE,
  BREAKDOWN,
  FOCUSED_BREAKDOWN_SEGMENTS,
  FORCE_UPDATE_EXPORT_COLUMN_NAMES,
  LEVEL_CONFIG,
  LEVELS,
  PMS_SYNC,
  SELECTED_CURRENCY,
  STAY_DATE_RANGE,
} from './constant';

/**
 *  Get relative levels based on parent and selected values.
 * @param {*} parent
 * @param {*} selected
 * @returns
 */
export const getRelativeLevels = (parent, selected, currentLevelValue) => {
  if (!selected) return null;
  // recursively find matching path
  const findPath = (node, selectedValue, matchKey, path = []) => {
    const currentPath = [...path, node[matchKey]];
    // matched node
    if (node[matchKey] === selectedValue) {
      return currentPath;
    }
    // no children
    if (!Array.isArray(node.children)) {
      return null;
    }
    let foundPath = null;
    // search children
    node.children.some((child) => {
      const result = findPath(child, selectedValue, matchKey, currentPath);

      if (result) {
        foundPath = result;
        return true;
      }
      return false;
    });
    //
    return foundPath;
  };
  // no parent, return selected values directly
  if (!parent) {
    return LEVELS.reduce((acc, level) => {
      acc[level.toLowerCase()] = selected[level] || null;
      return acc;
    }, {});
  }
  // find selected level
  const parentLevel = LEVELS.find((level) => selected[level] !== undefined);
  if (!parentLevel) {
    return {
      continent: null,
      country: null,
      state: null,
      city: null,
    };
  }
  // get hierarchy path
  const normalizedLevels = LEVELS.map((level) => level.toLowerCase());
  //
  const path = findPath(parent, selected[parentLevel], parentLevel);
  const startIndex = LEVELS.indexOf(parentLevel);
  const currentLevelIndex = normalizedLevels.indexOf(currentLevelValue);
  //
  return Object.fromEntries(
    normalizedLevels.map((level, index) => {
      const pathValue = index >= startIndex ? path?.[index - startIndex] ?? null : null;

      return [level, currentLevelIndex >= index && !pathValue ? 'Unknown' : pathValue];
    })
  );
};

/**
 * Returns previous and next hierarchy values
 *
 * @param {string} value
 * @returns {{
 *   current: string | null,
 *   next: string | null
 * }}
 */
export const getHierarchyNeighbors = (value) => {
  const hierarchy = ['continent', 'country', 'state', 'city'];
  const index = hierarchy.indexOf(value);

  if (index === -1) {
    return {
      previous: null,
      next: null,
    };
  }
  //
  return {
    previous: index > 0 ? hierarchy[index - 1] : null,
    next: index < hierarchy.length - 1 ? hierarchy[index + 1] : null,
  };
};

const buildLevelFilters = (selection, level) => {
  const levelIndex = LEVEL_CONFIG.findIndex((l) => l.key === level);
  if (levelIndex <= 0) return {};
  return LEVEL_CONFIG.slice(0, levelIndex).reduce((acc, { key, name }) => {
    const filter = createCeruleanFilter(name, selection?.[key] ?? null);
    return { ...acc, ...filter };
  }, {});
};

export const hasAnyLevelSelected = (regionalMapSelection = {}) =>
  Object.values(regionalMapSelection).some(
    (value) => value !== null && value !== undefined && value !== ''
  );

export const buildRegionalFilters = ({
  bookingDate,
  targetStartDate,
  targetEndDate,
  currencyType,
  hotels,
  pms,
  visualFilters,
  chartBreakdown,
  isDetailed,
  focusOn,
  regionalMapLevel,
  regionalMapSelection,
}) => {
  //
  const focusOnLabelList = focusOn?.map((element) => element?.label);
  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);

  //
  return {
    ...createCeruleanDateFilter(STAY_DATE_LOWER, targetStartDate),
    ...createCeruleanDateFilter(STAY_DATE_UPPER, targetEndDate),
    ...createCeruleanDateFilter(BOOKING_DATE_LOWER, bookingDate.startDate),
    ...createCeruleanDateFilter(BOOKING_DATE_UPPER, bookingDate.endDate),
    ...createCeruleanFilter(CURRENCY_FILTER, currencyType),
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),
    ...createCeruleanFilter(PMS_FILTER, pms?.toString()),
    ...createCeruleanBreakdownSchemaFilter({ visualFilters, chartBreakdown, isDetailed }),
    ...createCeruleanFilter(BREAKDOWN_LIST_FILTER, focusOnLabelList),
    ...createCeruleanFilter('levelFilter', hasAnyLevelSelected(regionalMapSelection)),
    ...buildLevelFilters(regionalMapSelection, regionalMapLevel),
  };
};

export const buildBreakdownFilters = ({
  bookingDate,
  targetStartDate,
  targetEndDate,
  currencyType,
  hotels,
  pms,
  visualFilters,
  chartBreakdown,
  isDetailed,
  focusOn,
}) => {
  //
  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);
  const focusOnLabelList = focusOn?.map((element) => element?.label);

  //
  return {
    ...createCeruleanDateFilter(STAY_DATE_LOWER, targetStartDate),
    ...createCeruleanDateFilter(STAY_DATE_UPPER, targetEndDate),
    ...createCeruleanDateFilter(BOOKING_DATE_LOWER, bookingDate.startDate),
    ...createCeruleanDateFilter(BOOKING_DATE_UPPER, bookingDate.endDate),
    ...createCeruleanFilter(CURRENCY_FILTER, currencyType),
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),
    ...createCeruleanFilter(PMS_FILTER, pms?.toString()),
    ...createCeruleanBreakdownSchemaFilter({ visualFilters, chartBreakdown, isDetailed }),
    ...createCeruleanFilter(BREAKDOWN_LIST_FILTER, focusOnLabelList),
    ...createCeruleanDateFilter(COMPARE_STAY_DATE_LOWER, targetStartDate),
    ...createCeruleanDateFilter(COMPARE_STAY_DATE_UPPER, targetEndDate),
    ...createCeruleanFilter(HOTEL_COMP_FILTER, hotelIds),
  };
};

export const hasKeyInObject = (arr, key) =>
  arr.some((obj) => Object.prototype.hasOwnProperty.call(obj, key));

export const toTitleCase = (str) =>
  str
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const buildColumnsFromData = (breakdownName, data = []) => {
  const order = ['Revenue', 'Room Count', 'ADR'];
  if (!data?.length) return [];

  const row = data[0];
  const columnMap = new Map();
  const firstColumnKey = Object.keys(row)[0]; // e.g. "State"

  const columns = [];

  // First fixed column
  columns.push({
    id: breakdownName,
    sorting: 'disabled',
    label: toTitleCase(breakdownName),
    children: [
      {
        id: firstColumnKey,
        label: toTitleCase(firstColumnKey),
        align: 'center',
        type: 'string',
      },
    ],
  });

  // Group remaining columns
  Object.keys(row)
    .sort()
    .forEach((key) => {
      if (key === firstColumnKey) return;

      const lastUnderscoreIndex = key.lastIndexOf('_');
      if (lastUnderscoreIndex === -1) return;

      const parentRaw = key.slice(0, lastUnderscoreIndex);
      const childRaw = key.slice(lastUnderscoreIndex + 1);

      if (!columnMap.has(parentRaw)) {
        columnMap.set(parentRaw, []);
      }

      columnMap.get(parentRaw).push({
        id: key,
        align: 'right',
        sorting: 'disabled',
        label: childRaw,
        type: 'string',
      });
    });

  const dynamicColumns = Array.from(columnMap.entries()).map(([parent, children]) => ({
    id: parent,
    label: parent,
    sorting: 'disabled',
    children: children.sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label)),
  }));

  return [...columns, ...dynamicColumns];
};
export const buildExportColumnsFromData = (breakdownName, data = []) => {
  if (!data?.length) return [];

  const row = data[0];
  return Object.keys(row).map((key) => ({
    id: key,
    label: FORCE_UPDATE_EXPORT_COLUMN_NAMES.includes(key) ? breakdownName : key,
    type: 'string',
  }));
};
/**
 * Replace matching object keys with a new key.
 *
 * @param {Array<Object>} data - Object array
 * @param {string} newKey - New key name
 * @param {string[]} keysToReplace - Keys to replace
 * @returns {Array<Object>}
 */
export const replaceKeys = (
  data,
  newKey,
  keysToReplace = ['Continent', 'Country', 'State', 'City']
) =>
  data.map((item) => {
    const updatedItem = { ...item };

    keysToReplace.forEach((key) => {
      if (key in updatedItem) {
        updatedItem[newKey] = updatedItem[key];
        delete updatedItem[key];
      }
    });

    return updatedItem;
  });
/**
 * Get hierarchy level key and value from object.
 *
 * @param {Object} item
 * @returns {{ key: string, value: any } | null}
 */
export const getLevel = (item) => {
  const levels = ['Continent', 'Country', 'State', 'City'];
  const foundKey = levels.find((key) => key in item);
  //
  return foundKey ? { key: foundKey.toLowerCase(), value: item[foundKey] } : null;
};

/**
 * Sort regional data rows by Continent (non-mutating).
 * @param {*} payload
 * @returns {*}
 */
export const sortByContinent = (payload) =>
  Array.isArray(payload)
    ? [...payload].sort((a, b) => a?.Continent.localeCompare(b?.Continent))
    : payload;

/**
 * Drill-down merge: walk `path` (values matched against `matchKey`),
 * keep only matching nodes per level, and set `children` at the leaf.
 * Mirrors the original drill-down behaviour of dropping non-matching siblings.
 *
 * @param {Array<Object>} data
 * @param {Array<*>} path
 * @param {Array<Object>} children
 * @param {string} matchKey
 * @returns {Array<Object>}
 */
export const mergeAtSelectedPath = (data, path, children, matchKey = 'Continent') => {
  const [head, ...rest] = path;
  return data
    ?.filter((item) => item?.[matchKey] === head)
    ?.map((item) =>
      rest.length
        ? { ...item, children: mergeAtSelectedPath(item?.children, rest, children, matchKey) }
        : { ...item, children }
    );
};

/**
 * Set `children` on items whose `matchKey` equals `matchValue`,
 * leaving every other item untouched (non-drill-down merge semantics).
 *
 * @param {Array<Object>} data
 * @param {string} matchKey
 * @param {*} matchValue
 * @param {Array<Object>} children
 * @returns {Array<Object>}
 */
export const mergeIntoMatchingNodes = (data, matchKey, matchValue, children) =>
  data?.map((item) => (item?.[matchKey] === matchValue ? { ...item, children } : item));

/**
 * Applies an incoming payload to regional data in drill-down mode.
 *
 * @param {Array<Object>} regionalData - existing drill-down tree (every level re-keyed to 'Continent')
 * @param {Array<Object>} payload - incoming rows for the drilled level
 * @param {{continent: *, country: *, state: *, city: *}} selection
 * @returns {Array<Object>}
 */
export const applyDrillDownData = (regionalData, payload, selection) => {
  const { continent, country, state: stateValue } = selection ?? {};
  if (continent && country && stateValue) {
    return mergeAtSelectedPath(
      regionalData,
      [continent, country, stateValue],
      replaceKeys(payload, 'Continent')
    );
  }
  if (continent && country) {
    return mergeAtSelectedPath(
      regionalData,
      [continent, country],
      replaceKeys(payload, 'Continent')
    );
  }
  if (country || stateValue) {
    return payload;
  }
  if (continent) {
    return mergeAtSelectedPath(regionalData, [continent], replaceKeys(payload, 'Continent'));
  }
  return regionalData;
};

/**
 * Applies an incoming payload to regional data in merge (non-drill-down) mode.
 * The merge level (Continent / Country / State) is derived from the keys of
 * the existing data, matching the previous reducer behaviour.
 *
 * @param {Array<Object>} regionalData
 * @param {Array<Object>} payload
 * @param {{continent: *, country: *, state: *, city: *}} selection
 * @returns {Array<Object>}
 */
export const applyMergedLevelData = (regionalData, payload, selection) => {
  const { continent, country, state: stateValue } = selection ?? {};
  if (hasKeyInObject(regionalData, 'Continent')) {
    const data = replaceKeys(payload, 'Continent').filter((item) => item.Continent !== 'Total');
    if (continent && country && stateValue) {
      return regionalData?.map((continentItem) =>
        continentItem?.Continent !== continent
          ? continentItem
          : {
              ...continentItem,
              children: continentItem?.children?.map((countryItem) =>
                countryItem?.Continent !== country
                  ? countryItem
                  : {
                      ...countryItem,
                      children: mergeIntoMatchingNodes(
                        countryItem?.children,
                        'Continent',
                        stateValue,
                        data
                      ),
                    }
              ),
            }
      );
    }
    if (continent && country) {
      return regionalData?.map((continentItem) =>
        continentItem?.Continent !== continent
          ? continentItem
          : {
              ...continentItem,
              children: mergeIntoMatchingNodes(continentItem?.children, 'Continent', country, data),
            }
      );
    }
    return mergeIntoMatchingNodes(regionalData, 'Continent', continent, data);
  }
  if (hasKeyInObject(regionalData, 'Country')) {
    const data = replaceKeys(payload, 'Country').filter((item) => item.Country !== 'Total');
    if (country && stateValue) {
      return regionalData?.map((countryItem) =>
        countryItem?.Country !== country
          ? countryItem
          : {
              ...countryItem,
              children: mergeIntoMatchingNodes(countryItem?.children, 'State', stateValue, data),
            }
      );
    }
    return mergeIntoMatchingNodes(regionalData, 'Country', country, data);
  }
  if (hasKeyInObject(regionalData, 'State')) {
    const data = replaceKeys(payload, 'State').filter((item) => item.State !== 'Total');
    if (stateValue) {
      return mergeIntoMatchingNodes(regionalData, 'State', stateValue, data);
    }
  }
  return regionalData;
};
// Get breakdown value from visual filters based on chart breakdown
export const getBreakdownValue = (visualFilters, chartBreakdown, isDetailed) => {
  const value = visualFilters?.filter?.((filter) => filter?.id?.includes(chartBreakdown))[0]
    ?.values?.[0].value;
  if (value === 'Channel (raw)') {
    return isDetailed ? 'Channel' : value;
  }
  if (value === 'Market Segment (raw)') {
    return isDetailed ? value : 'Market Segment';
  }
  return value;
};
//
export const buildHeaderFilterData = ({
  bookingDate,
  targetStartDate,
  targetEndDate,
  currencyType,
  hotels,
  pms,
  visualFilters,
  chartBreakdown,
  isDetailed,
  focusOn,
}) => {
  const stayDateRange = `${format(new Date(targetStartDate), YEAR_MONTH_DAY_PATTERN)} to ${format(
    new Date(targetEndDate),
    YEAR_MONTH_DAY_PATTERN
  )}`;
  //
  const focusOnLabelList = focusOn?.map((element) => element?.label);
  const bookingDateRange = `${format(
    new Date(bookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(bookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  return {
    title: 'Regional Map',
    hotelName: hotels?.label,
    sections: [
      { title: STAY_DATE_RANGE, value: stayDateRange, id: 1 },
      { title: BOOKING_DATE_RANGE, value: bookingDateRange, id: 2 },
      { title: PMS_SYNC, value: pms ? 'On' : 'Off', id: 3 },
      { title: SELECTED_CURRENCY, value: currencyType, id: 4 },
      {
        title: BREAKDOWN,
        value: getBreakdownValue(visualFilters, chartBreakdown, isDetailed),
        id: 5,
      },
      { title: FOCUSED_BREAKDOWN_SEGMENTS, value: focusOnLabelList?.join('/'), id: 6 },
    ],
  };
};
/**
 * Generate CSV data from regional map report visual.
 *
 * @param {Object} params
 * @param {Object} params.regionalMapReport
 * @param {boolean} params.isLoaded
 * @param {string} params.selectedBreakdown
 * @param {Object} params.currentUser
 * @param {boolean} params.enableHotelGroups
 * @param {Object} params.targetDate
 * @param {Object} params.bookingDate
 * @param {Object} params.selectedHotel
 * @param {boolean} params.pmsSyncToggle
 * @param {Array} params.focusOn
 * @param {string} params.localCurrency
 *
 * @returns {Promise<string|null>} CSV string or null
 */
const buildRegionalVisualExcelData = async ({
  regionalMapReport,
  isLoaded,
  selectedBreakdown,
  currentUser,
  enableHotelGroups,
  targetDate,
  bookingDate,
  selectedHotel,
  pmsSyncToggle,
  focusOn,
  localCurrency,
  isInterpreterAgent = false,
}) => {
  if (!isEmpty(regionalMapReport) && isLoaded) {
    // Get visual data from report instance
    const pages = await regionalMapReport.getPages();
    const page = pages.filter((p) => p.isActive)[0];
    const visuals = await page.getVisuals();
    const visual = visuals.filter((v) => v.name === visuals[0]?.name)[0];
    const result = await visual.exportData(models.ExportDataType.Summarized);
    // Format obtained data to download
    if (result?.data?.length > 0) {
      const dataArray = String(result?.data).split('\r\n');
      const header = dataArray[0];
      const mapData = dataArray
        .slice(1)
        .map((data) => String(data).split(','))
        .filter((data) => data[0] !== '');
      if (isEmpty(mapData)) {
        throw new Error('Empty Map Data');
      } else {
        const regionalMapData = mapData.join('\r\n');
        const username = isInterpreterAgent ? '' : `Username : , ${currentUser?.username}\r\n`;
        const reportGenerationDate = isInterpreterAgent
          ? ''
          : `Report Generation Date : ,"${formatDate(
              new Date(),
              YEAR_MONTH_DAY_PATTERN
            )}, ${formatDate(new Date(), TIME_PATTERN)}"\r\n`;
        const hotel = enableHotelGroups
          ? `Hotel Set: ,${selectedHotel?.label}\r\n`
          : `Hotel: ,${selectedHotel?.label}\r\n`;
        const stayDateRange = `Stay Date Range :,${formatDate(
          targetDate.startDate,
          YEAR_MONTH_DAY_PATTERN
        )} to ${formatDate(targetDate.endDate, YEAR_MONTH_DAY_PATTERN)}\r\n`;
        const pmsSync = `PMS Sync : , ${pmsSyncToggle ? 'On' : 'Off'}\r\n`;
        const bookingDateRange = `Booking Date Range : ,${formatDate(
          bookingDate.startDate,
          YEAR_MONTH_DAY_PATTERN
        )} to ${formatDate(bookingDate.endDate, YEAR_MONTH_DAY_PATTERN)}\r\n`;
        const breakdown = `Breakdown : ,${selectedBreakdown}\r\n`;
        const focus = focusOn ? focusOn?.map((item) => item?.label).toString() : '';
        const focusedBreakdown = `Focused Breakdown Segments: ,${focus
          .toString()
          .replaceAll(',', '/')}\r\n`;
        const currency = `Selected Currency : , ${localCurrency}\r\n`;

        const csv = username.concat(
          reportGenerationDate,
          hotel,
          stayDateRange,
          pmsSync,
          bookingDateRange,
          currency,
          breakdown,
          focusedBreakdown,
          '\r\n',
          header,
          '\r\n',
          regionalMapData
        );
        return csv;
      }
    }
  }
  return null;
};
// Recursive function to get the maximum level of nesting in the data
const getMaxLevel = (items, level = 0) =>
  Math.max(
    level,
    ...items.map((item) => (item.children?.length ? getMaxLevel(item.children, level + 1) : level))
  );
// Utility function to determine the last level of regional map data based on its structure
export const getRegionalMapDataLastLevel = (data) => {
  const levelName = LEVELS.find((level) => data?.[0][level] !== undefined);
  const maxLevel = getMaxLevel(data);

  if (levelName === 'Continent') {
    if (maxLevel === 3) return 'city';
    if (maxLevel === 2) return 'state';
    if (maxLevel === 1) return 'country';
    if (maxLevel === 0) return 'continent';
  } else if (levelName === 'Country') {
    if (maxLevel === 2) return 'city';
    if (maxLevel === 1) return 'state';
    if (maxLevel === 0) return 'country';
  } else if (levelName === 'State') {
    if (maxLevel === 1) return 'city';
    if (maxLevel === 0) return 'state';
  }
  return 'city';
};
// Utility function to remove the last level of regional map data based on its structure
export const removeMaxLevel = (items) => {
  const maxLevel = getMaxLevel(items);

  const remove = (data, level = 0) =>
    data.map((item) => {
      let children = [];

      if (level + 1 !== maxLevel && item.children?.length) {
        children = remove(item.children, level + 1);
      }

      return {
        ...item,
        children,
      };
    });

  return remove(items);
};
// Utility function to remove values from a Set that end with a specific suffix
export const removeSetValueBySuffix = (set, suffix) =>
  new Set([...set].filter((value) => !value.endsWith(suffix)));

export const ensureRegionalMapFullDataFilters = (filters) => {
  const defaultFilters = {
    contintentLevelFilter: {
      target: {
        table: 'measures',
        columns: 'dynamic_measure',
      },
      conditions: {
        operator: 'In',
        values: 'North America',
      },
    },
    stateLevelFilter: {
      target: {
        table: 'measures',
        columns: 'dynamic_measure',
      },
      conditions: {
        operator: 'In',
        values: null,
      },
    },
    countryLevelFilter: {
      target: {
        table: 'measures',
        columns: 'dynamic_measure',
      },
      conditions: {
        operator: 'In',
        values: null,
      },
    },
  };

  Object.entries(defaultFilters).forEach(([key, value]) => {
    if (!(key in filters)) {
      filters[key] = value;
    }
  });

  return filters;
};

export default buildRegionalVisualExcelData;
